import CardDisputeAction from "./CardDisputeAction";
import AdminPermission from "components/admin/AdminPermission";
import useAdminAccess from "hooks/useAdminAccess";
import {
  httpDeleteVirtualCard,
  httpFreezeVirtualCard,
  httpGetVirtualCardDetail,
  httpGetVirtualCardDisputes,
  httpGetVirtualCardTransactions,
} from "api/admin/virtual-card";
import AdminSidebar from "components/layout/AdminSidebar";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import Pagination from "components/ui/Pagination";
import Spinner from "components/ui/Spinner";
import { DateTime } from "luxon";
import { type ReactNode, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { showDanger, showSuccess } from "utilities/misc";

const limit = 20;

const VirtualCardDetails: React.FC = () => {
  const { can } = useAdminAccess();
  const { virtualCardId, userId } = useParams<{
    virtualCardId: string;
    userId: string;
  }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [disputePage, setDisputePage] = useState(1);
  const [freezeReason, setFreezeReason] = useState("");
  const [terminateReason, setTerminateReason] = useState("");

  const { data: card, isLoading: isLoadingCard } = useQuery(
    ["virtualCardDetail", virtualCardId],
    () => httpGetVirtualCardDetail(virtualCardId || ""),
    { enabled: !!virtualCardId },
  );
  const { data: transactionData, isLoading: isLoadingTransactions } = useQuery(
    ["virtualCardTransactions", virtualCardId, currentPage],
    () =>
      httpGetVirtualCardTransactions(virtualCardId || "", currentPage, limit),
    {
      enabled:
        !!virtualCardId &&
        card?.isLinked === true &&
        can("virtualCard.transactions.view"),
    },
  );
  const { data: disputesData, isLoading: isLoadingDisputes } = useQuery(
    ["virtualCardDisputes", virtualCardId, disputePage],
    () => httpGetVirtualCardDisputes(virtualCardId || "", disputePage, limit),
    {
      enabled:
        !!virtualCardId &&
        card?.isLinked === true &&
        can("virtualCard.disputes.view"),
    },
  );

  const freezeMutation = useMutation(
    ({ freeze, reason }: { freeze: boolean; reason?: string }) =>
      httpFreezeVirtualCard(virtualCardId || "", freeze, reason),
    {
      onSuccess: () => {
        showSuccess("Card status updated");
        setFreezeReason("");
        queryClient.invalidateQueries(["virtualCardDetail", virtualCardId]);
      },
    },
  );
  const terminateMutation = useMutation(
    (reason: string) => httpDeleteVirtualCard(virtualCardId || "", reason),
    {
      onSuccess: () => {
        showSuccess("Card terminated");
        setTerminateReason("");
        queryClient.invalidateQueries(["virtualCardDetail", virtualCardId]);
      },
    },
  );

  const transactions = transactionData?.transactions || [];
  const pagination = transactionData?.pagination || {};
  const disputes = disputesData?.disputes || [];
  const disputePagination = disputesData?.pagination || {};

  const handleBackClick = () => {
    if (userId && userId !== "all") return navigate(`/virtual-cards/${userId}`);
    navigate("/virtual-cards");
  };

  const freezeCard = () => {
    if (!freezeReason.trim()) return showDanger("reason is required");
    freezeMutation.mutate({ freeze: true, reason: freezeReason.trim() });
  };

  const unfreezeCard = () => {
    freezeMutation.mutate({ freeze: false });
  };

  const terminateCard = () => {
    if (!terminateReason.trim()) return showDanger("reason is required");
    if (
      !window.confirm("Terminate this virtual card? This cannot be undone.")
    ) {
      return;
    }
    terminateMutation.mutate(terminateReason.trim());
  };

  return (
    <Container>
      <AdminSidebar />
      <Main className="pb-10 mx-5 mt-5 text-left">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={handleBackClick}
            className="px-4 py-2 text-black bg-gray-200 rounded hover:bg-gray-300"
            type="button"
          >
            Back
          </button>
          <h3 className="text-2xl">Virtual Card Detail</h3>
        </div>

        {isLoadingCard ? (
          <div className="flex justify-center py-10">
            <Spinner className="text-4xl" />
          </div>
        ) : (
          <>
            {card?.isLinked === false && (
              <ReadOnlyNotice
                title="Miden-only card"
                message={
                  card?.readOnlyReason ||
                  "This card exists on Miden but is not linked to a local Bitoshi record."
                }
              />
            )}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
              <Summary label="Card ID" value={card?.id || virtualCardId} />
              <Summary label="Miden Card ID" value={card?.midenCardId || "-"} />
              <Summary
                label="Cardholder"
                value={card?.cardholderUsername || "-"}
              />
              <Summary label="Type" value={card?.cardType || "-"} />
              <Summary label="Status" value={card?.status || "-"} />
              <Summary
                label="Masked Number"
                value={card?.maskedCardNumber || "-"}
              />
              <Summary label="Expiry" value={card?.expiry || "-"} />
              <Summary
                label="Created"
                value={
                  card?.createdAt
                    ? DateTime.fromISO(card.createdAt).toFormat("DD t")
                    : "-"
                }
              />
              <Summary
                label="Balance"
                value={`${card?.currency || "USD"} ${card?.balance || 0}`}
              />
              <Summary
                label="Admin Freeze"
                value={
                  card?.isLinked === false
                    ? "Unavailable"
                    : card?.isAdminFrozen
                      ? "Frozen"
                      : "Not Frozen"
                }
              />
            </div>
          </>
        )}

        <AdminPermission
          permission={[
            "virtualCard.cards.freeze",
            "virtualCard.cards.unfreeze",
            "virtualCard.cards.delete",
          ]}
        >
          <section className="p-5 mt-8 bg-white border border-gray-200 rounded">
            <h4 className="mb-4 text-lg font-medium">Card Actions</h4>
            {card?.isLinked === false ? (
              <ReadOnlyNotice
                title="Actions unavailable"
                message="Freeze, unfreeze, and terminate actions require a local Bitoshi card record for ownership and audit logging."
              />
            ) : (
              <div className="grid gap-5 md:grid-cols-2">
                <AdminPermission
                  permission={[
                    "virtualCard.cards.freeze",
                    "virtualCard.cards.unfreeze",
                  ]}
                >
                  <div>
                    <label className="text-sm text-gray-600">
                      Freeze reason
                    </label>
                    <textarea
                      className="w-full h-24 p-3 mt-1 text-sm border border-gray-300 rounded"
                      onChange={(event) => setFreezeReason(event.target.value)}
                      value={freezeReason}
                    />
                    <div className="flex gap-3 mt-3">
                      <AdminPermission permission={"virtualCard.cards.freeze"}>
                        <button
                          className="px-4 py-2 text-white bg-black rounded disabled:opacity-50"
                          disabled={
                            !freezeReason.trim() || freezeMutation.isLoading
                          }
                          onClick={freezeCard}
                          type="button"
                        >
                          Freeze
                        </button>
                      </AdminPermission>
                      <AdminPermission
                        permission={"virtualCard.cards.unfreeze"}
                      >
                        <button
                          className="px-4 py-2 text-white bg-gray-700 rounded disabled:opacity-50"
                          disabled={freezeMutation.isLoading}
                          onClick={unfreezeCard}
                          type="button"
                        >
                          Unfreeze
                        </button>
                      </AdminPermission>
                    </div>
                  </div>
                </AdminPermission>
                <AdminPermission permission={"virtualCard.cards.delete"}>
                  <div>
                    <label className="text-sm text-gray-600">
                      Terminate reason
                    </label>
                    <textarea
                      className="w-full h-24 p-3 mt-1 text-sm border border-gray-300 rounded"
                      onChange={(event) =>
                        setTerminateReason(event.target.value)
                      }
                      value={terminateReason}
                    />
                    <AdminPermission permission={"virtualCard.cards.delete"}>
                      <button
                        className="px-4 py-2 mt-3 text-white bg-red-600 rounded disabled:opacity-50"
                        disabled={
                          !terminateReason.trim() || terminateMutation.isLoading
                        }
                        onClick={terminateCard}
                        type="button"
                      >
                        Terminate
                      </button>
                    </AdminPermission>
                  </div>
                </AdminPermission>
              </div>
            )}
          </section>
        </AdminPermission>

        <AdminPermission permission={"virtualCard.transactions.view"}>
          <section className="p-5 mt-8 bg-white border border-gray-200 rounded">
            <h4 className="mb-4 text-lg font-medium">Transaction History</h4>
            {card?.isLinked === false ? (
              <ReadOnlyNotice
                title="Transaction history unavailable"
                message="Local transaction history is only available for cards linked to a Bitoshi user record."
              />
            ) : isLoadingTransactions ? (
              <Spinner />
            ) : transactions.length === 0 ? (
              <p className="py-8 text-center text-gray-500">
                No transactions found
              </p>
            ) : (
              <>
                <DataTable
                  headers={[
                    "Transaction",
                    "Merchant",
                    "Amount",
                    "Date",
                    "Status",
                    "References",
                    "Dispute",
                  ]}
                  rows={transactions.map((transaction: any) => [
                    <div>
                      <p className="font-medium text-gray-900">
                        {formatTransactionType(transaction.type)}
                      </p>
                      <p className="max-w-xs mt-1 text-xs leading-5 text-gray-500">
                        {transaction.description || "-"}
                      </p>
                    </div>,
                    <MerchantCell transaction={transaction} />,
                    <span className="font-medium text-gray-900">
                      {formatCardAmount(transaction.amount, card?.currency)}
                    </span>,
                    <div className="min-w-[140px]">
                      <p className="text-gray-900">
                        {formatDate(
                          transaction.timestamp || transaction.createdAt,
                        )}
                      </p>
                      {transaction.timestamp && transaction.createdAt && (
                        <p className="mt-1 text-xs text-gray-500">
                          Recorded {formatDate(transaction.createdAt)}
                        </p>
                      )}
                    </div>,
                    <StatusBadge status={transaction.status} />,
                    <div className="space-y-1 text-xs text-gray-600">
                      <ReferenceLine label="ID" value={transaction.id} />
                      <ReferenceLine
                        label="Txn"
                        value={transaction.transactionID}
                      />
                      <ReferenceLine
                        label="External"
                        value={transaction.externalTransactionID}
                      />
                    </div>,
                    <CardDisputeAction
                      cardId={virtualCardId!}
                      transactionId={transaction.id}
                    />,
                  ])}
                />
                {pagination.totalPages > 1 && (
                  <Pagination
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    limit={limit}
                    page={currentPage}
                    total={pagination.total}
                  />
                )}
              </>
            )}
          </section>
        </AdminPermission>

        <AdminPermission permission={"virtualCard.disputes.view"}>
          <section className="p-5 mt-8 bg-white border border-gray-200 rounded">
            <h4 className="mb-4 text-lg font-medium">Disputes</h4>
            {card?.isLinked === false ? (
              <ReadOnlyNotice
                title="Disputes unavailable"
                message="Disputes require local virtual-card transactions, so they are disabled for Miden-only cards."
              />
            ) : isLoadingDisputes ? (
              <Spinner />
            ) : disputes.length === 0 ? (
              <p className="py-8 text-center text-gray-500">
                No disputes found
              </p>
            ) : (
              <>
                <DataTable
                  headers={["Description", "Date Raised", "Status", "Action"]}
                  rows={disputes.map((dispute: any) => [
                    dispute.description || "-",
                    dispute.raisedAt
                      ? DateTime.fromISO(dispute.raisedAt).toFormat("DD t")
                      : "-",
                    dispute.status,
                    <CardDisputeAction
                      key={`${dispute.id}-${dispute.status}`}
                      cardId={virtualCardId!}
                      dispute={dispute}
                    />,
                  ])}
                />
                {disputePagination.totalPages > 1 && (
                  <Pagination
                    setCurrentPage={setDisputePage}
                    currentPage={disputePage}
                    limit={limit}
                    page={disputePage}
                    total={disputePagination.total}
                  />
                )}
              </>
            )}
          </section>
        </AdminPermission>
      </Main>
    </Container>
  );
};

const Summary: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="p-4 bg-white border border-gray-200 rounded">
    <p className="text-sm text-gray-500">{label}</p>
    <p className="mt-2 text-lg font-medium break-words">{value}</p>
  </div>
);

const ReadOnlyNotice: React.FC<{ title: string; message: string }> = ({
  title,
  message,
}) => (
  <div className="p-4 mb-5 border rounded border-amber-200 bg-amber-50">
    <p className="font-medium text-amber-900">{title}</p>
    <p className="mt-1 text-sm leading-6 text-amber-800">{message}</p>
  </div>
);

const formatTransactionType = (type?: string) => {
  if (!type) return "-";
  return type
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatCardAmount = (amount: string | number, currency?: string) => {
  const numericAmount = Number(amount);
  const formattedAmount = Number.isFinite(numericAmount)
    ? numericAmount.toLocaleString(undefined, {
        minimumFractionDigits: numericAmount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      })
    : amount || "-";

  return `${currency || "USD"} ${formattedAmount}`;
};

const formatDate = (value?: string) =>
  value ? DateTime.fromISO(value).toFormat("DD t") : "-";

const truncateReference = (value?: string) => {
  if (!value) return "-";
  if (value.length <= 18) return value;
  return `${value.slice(0, 8)}...${value.slice(-6)}`;
};

const getMerchantName = (transaction: any) =>
  transaction.merchant?.name || transaction.merchantName || "-";

const getMerchantMeta = (transaction: any) =>
  [
    transaction.merchant?.city,
    transaction.merchant?.state,
    transaction.merchant?.mcc || transaction.merchantMcc
      ? `MCC ${transaction.merchant?.mcc || transaction.merchantMcc}`
      : null,
    transaction.merchant?.mid ? `MID ${transaction.merchant.mid}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

const MerchantCell: React.FC<{ transaction: any }> = ({ transaction }) => {
  const merchantMeta = getMerchantMeta(transaction);

  return (
    <div className="min-w-[180px]">
      <p className="font-medium text-gray-900">
        {getMerchantName(transaction)}
      </p>
      {merchantMeta && (
        <p className="max-w-xs mt-1 text-xs leading-5 text-gray-500">
          {merchantMeta}
        </p>
      )}
    </div>
  );
};

const StatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  const normalizedStatus = (status || "-").toLowerCase();
  const colorClass =
    normalizedStatus === "successful" || normalizedStatus === "settled"
      ? "bg-green-50 text-green-700 ring-green-200"
      : normalizedStatus === "failed" || normalizedStatus === "declined"
        ? "bg-red-50 text-red-700 ring-red-200"
        : "bg-gray-50 text-gray-700 ring-gray-200";

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium capitalize ring-1 ring-inset ${colorClass}`}
    >
      {status || "-"}
    </span>
  );
};

const ReferenceLine: React.FC<{ label: string; value?: string }> = ({
  label,
  value,
}) => (
  <p className="whitespace-nowrap">
    <span className="font-medium text-gray-500">{label}:</span>{" "}
    <span title={value || undefined}>{truncateReference(value)}</span>
  </p>
);

const DataTable: React.FC<{ headers: string[]; rows: ReactNode[][] }> = ({
  headers,
  rows,
}) => (
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead className="bg-gray-100">
        <tr>
          {headers.map((header) => (
            <th className="px-4 py-3 text-sm text-left" key={header}>
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr className="border-b" key={index}>
            {row.map((cell, cellIndex) => (
              <td
                className="px-4 py-4 text-sm text-gray-700 align-top"
                key={cellIndex}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default VirtualCardDetails;
